import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchQuarantine } from "../../../services/aro/quarantineService";
import { QuarantineListItem } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import { formatDateTime, formatLabel } from "../../../utils/formatters";
import "./QuarantinePage.css";

const LIMIT = 50;

interface Props {
  onSelect: (id: number) => void;
}

function statusVariant(status: QuarantineListItem["status"]) {
  if (status === "promoted")  return "green";
  if (status === "discarded") return "gray";
  return "amber";
}

interface CommittedFilters {
  fileId: string;
  status: string;
  reason: string;
}

export default function QuarantinePage({ onSelect }: Props) {
  const location = useLocation();
  const initialFileId = new URLSearchParams(location.search).get("file_id") ?? "";

  const [rows,    setRows]    = useState<QuarantineListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // Input state — UI controls only
  const [fileId, setFileId] = useState(initialFileId);
  const [status, setStatus] = useState("all");
  const [reason, setReason] = useState("all");

  // Committed state — what the API uses (only changes on Search / Clear)
  const [committed, setCommitted] = useState<CommittedFilters>({
    fileId: initialFileId,
    status: "all",
    reason: "all",
  });

  const load = useCallback((off: number) => {
    setLoading(true);
    setError(null);
    fetchQuarantine({
      file_id: committed.fileId || undefined,
      status:  committed.status !== "all" ? committed.status : undefined,
      reason:  committed.reason !== "all" ? committed.reason : undefined,
      limit:   LIMIT,
      offset:  off,
    })
      .then((r) => { setRows(r.quarantine); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [committed]);

  useEffect(() => { setOffset(0); load(0); }, [load]);

  const handleSearch = () => {
    setCommitted({ fileId, status, reason });
  };

  const clearFilters = () => {
    setFileId(""); setStatus("all"); setReason("all");
    setCommitted({ fileId: "", status: "all", reason: "all" });
  };

  const from = total === 0 ? 0 : offset + 1;
  const to   = Math.min(offset + LIMIT, total);

  return (
    <div className="aro-page">
      <PageHeader title="Quarantine" subtitle={`${total} rows needing review`} />

      <div className="aro-filter-bar">
        <input
          className="aro-input"
          placeholder="File ID…"
          value={fileId}
          onChange={(e) => setFileId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <select className="aro-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="quarantined">Quarantined</option>
          <option value="promoted">Promoted</option>
          <option value="discarded">Discarded</option>
        </select>
        <select className="aro-select" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="all">All Reasons</option>
          <option value="missing_full_name">Missing Full Name</option>
          <option value="missing_email">Missing Email</option>
          <option value="missing_certificate_name">Missing Certificate Name</option>
          <option value="missing_earned_date">Missing Earned Date</option>
          <option value="missing_event_for_ai_week">Missing Event For AI Week</option>
          <option value="exception">Exception</option>
        </select>
        <button className="aro-btn aro-btn--primary" onClick={handleSearch}>Search</button>
        <button className="aro-btn aro-btn--ghost" onClick={clearFilters}>Clear</button>
      </div>

      {error && <div className="aro-error-banner">{error}</div>}

      <div className="aro-card aro-card--no-pad">
        {loading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <EmptyState icon="🚧" message="No quarantined rows found." />
        ) : (
          <table className="aro-table">
            <thead>
              <tr>
                <th>File ID</th>
                <th>Row #</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Resolved By</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  className={[
                    i % 2 === 1 ? "aro-table__row--odd" : "",
                    "aro-table__row--clickable",
                  ].join(" ")}
                  onClick={() => onSelect(r.id)}
                >
                  <td className="aro-muted aro-quarantine__file-id">{r.file_id}</td>
                  <td>{r.row_number}</td>
                  <td>
                    {formatLabel(r.reason)}
                    {r.reason_detail && <div className="aro-muted">{r.reason_detail}</div>}
                  </td>
                  <td><AROBadge label={formatLabel(r.status)} variant={statusVariant(r.status)} /></td>
                  <td className="aro-muted">{r.resolved_by || "—"}</td>
                  <td className="aro-muted">{formatDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > LIMIT && (
        <div className="aro-pagination">
          <span className="aro-pagination__info">Showing {from}–{to} of {total}</span>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset === 0}
            onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o); }}
          >
            ← Previous
          </button>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset + LIMIT >= total}
            onClick={() => { const o = offset + LIMIT; setOffset(o); load(o); }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
